import { Badge, Button, cn } from '@repo/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, Check } from 'lucide-react';
import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useMarkAllAsRead,
  useMarkAsRead,
  useNotifications,
} from '@/features/notifications/api/notifications';
import type { Notification } from '@/features/notifications/types';
import { sseManager } from '@/lib/sse-manager';
import { useAuthStore } from '@/stores/auth.store';

/**
 * NotificationBell — bell icon + dropdown with the latest 5 unread
 * notifications, SSE-driven badge.
 *
 * Behaviour:
 *   - SSE subscribes on mount; the bell increments the unread count
 *     when a new notification event arrives.
 *   - The list query (`useNotifications`) loads the cold-start state
 *     so a refresh never blanks the bell.
 *   - On dropdown open: if `unreadOnly` filter shows items, show them
 *     first; otherwise show the latest 5 of any state.
 *   - Per-row mark-as-read hits `useMarkAsRead` and optimistically
 *     decrements the local unread count.
 *   - Header CTA "mark all read" hits `useMarkAllAsRead`.
 *
 * The component renders inside the TopBar (mobile + desktop). The
 * dropdown is a positioned `<div>` (not Radix) — keeps the surface
 * small and avoids pulling in `@radix-ui/react-popover` for what is
 * effectively a 5-row list.
 */
export interface NotificationBellProps {
  className?: string;
}

interface BellState {
  unreadCount: number;
  liveNotifications: Notification[];
}

export function NotificationBell({ className }: NotificationBellProps): React.ReactElement {
  const qc = useQueryClient();
  const authed = useAuthStore((s) => Boolean(s.token && s.user));
  const { data, isLoading } = useNotifications({ limit: 50 });
  const markRead = useMarkAsRead();
  const markAll = useMarkAllAsRead();

  const [open, setOpen] = useState(false);
  const [state, setState] = useState<BellState>({ unreadCount: 0, liveNotifications: [] });

  // -----------------------------------------------------------------
  // SSE wiring — start the manager on mount and update state on each
  // notification event. Optimistic update on the live array; the
  // canonical TanStack Query cache stays in sync via setQueryData so
  // a refresh renders the same state.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!authed) return;
    sseManager.start();
    const offEvent = sseManager.subscribe((event) => {
      if (event.event !== 'notification') return;
      const notif = event.data as Notification;
      setState((prev) => ({
        unreadCount: notif.unread ? prev.unreadCount + 1 : prev.unreadCount,
        liveNotifications: [notif, ...prev.liveNotifications].slice(0, 50),
      }));
      // Mirror into the TanStack cache so a re-render of the bell
      // (e.g. switching routes) sees the new notification without
      // a fresh network round-trip.
      qc.setQueryData(
        ['notifications', 'list', { unreadOnly: false, limit: 50 }],
        (old: { data: Notification[]; total: number } | undefined) => {
          if (!old) return { data: [notif], total: 1 };
          const filtered = old.data.filter((n) => n.id !== notif.id);
          return { data: [notif, ...filtered], total: old.total + 1 };
        },
      );
    });
    const offState = sseManager.subscribeState((s) => {
      // The bell does not need to react to status transitions, but
      // the subscription here keeps the BroadcastChannel alive even
      // when the dropdown is closed.
      void s;
    });
    return () => {
      offEvent();
      offState();
    };
  }, [authed, qc]);

  // -----------------------------------------------------------------
  // Sync cold-start (from useNotifications) into the local state.
  // Runs whenever the query data changes — including after mark-as-read
  // invalidations.
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!data?.data) return;
    const unreadCount = data.data.filter((n) => n.unread).length;
    setState((prev) => ({ unreadCount, liveNotifications: prev.liveNotifications }));
  }, [data]);

  const visible = useMemo<Notification[]>(() => {
    const merged = mergeLatest(state.liveNotifications, data?.data ?? []);
    return merged.slice(0, 5);
  }, [state.liveNotifications, data]);

  const handleMarkAsRead = (id: string): void => {
    setState((prev) => ({
      unreadCount: Math.max(prev.unreadCount - 1, 0),
      liveNotifications: prev.liveNotifications.map((n) =>
        n.id === id ? { ...n, unread: false, readAt: new Date().toISOString() } : n,
      ),
    }));
    markRead.mutate(id);
  };

  const handleMarkAll = (): void => {
    setState((prev) => ({
      unreadCount: 0,
      liveNotifications: prev.liveNotifications.map((n) =>
        n.unread ? { ...n, unread: false, readAt: new Date().toISOString() } : n,
      ),
    }));
    markAll.mutate();
  };

  return (
    <div className={cn('relative', className)}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Notificaciones${state.unreadCount > 0 ? ` (${state.unreadCount} sin leer)` : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="relative"
      >
        <Bell className="size-4" aria-hidden="true" />
        {state.unreadCount > 0 ? (
          <Badge
            variant="destructive"
            className="absolute -right-0.5 -top-0.5 h-4 min-w-4 justify-center px-1 text-[10px] leading-none"
            aria-hidden="true"
          >
            {state.unreadCount > 99 ? '99+' : state.unreadCount}
          </Badge>
        ) : null}
      </Button>

      {open ? (
        <div
          role="menu"
          aria-label="Notificaciones recientes"
          className="absolute right-0 top-full z-50 mt-1 w-[340px] max-w-[calc(100vw-1.5rem)] rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-lg"
        >
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-xs font-semibold uppercase text-muted-foreground">
              Notificaciones
            </span>
            {state.unreadCount > 0 ? (
              <button
                type="button"
                onClick={handleMarkAll}
                className="text-xs font-medium text-primary hover:underline"
              >
                Marcar todo leído
              </button>
            ) : null}
          </div>

          <ul className="max-h-[360px] space-y-1 overflow-y-auto">
            {isLoading && visible.length === 0 ? (
              <li className="px-2 py-6 text-center text-xs text-muted-foreground">Cargando…</li>
            ) : visible.length === 0 ? (
              <li className="px-2 py-6 text-center text-xs text-muted-foreground">
                Sin notificaciones
              </li>
            ) : (
              visible.map((n) => (
                <li
                  key={n.id}
                  className={cn(
                    'flex items-start gap-2 rounded-sm px-2 py-2 text-sm transition-colors hover:bg-secondary/50',
                    n.unread ? 'bg-secondary/30' : '',
                  )}
                >
                  <Link
                    to={n.linkUrl ?? '/notifications'}
                    onClick={() => {
                      setOpen(false);
                      if (n.unread) handleMarkAsRead(n.id);
                    }}
                    className="min-w-0 flex-1"
                  >
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{n.title}</span>
                      {n.unread ? (
                        <span className="size-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                      ) : null}
                    </div>
                    <p className="line-clamp-2 text-xs text-muted-foreground">{n.body}</p>
                    <p className="mt-0.5 text-[10px] text-muted-foreground/70">
                      {categoryLabel(n.category)} · {relativeTime(n.createdAt)}
                    </p>
                  </Link>
                  {n.unread ? (
                    <button
                      type="button"
                      aria-label="Marcar como leída"
                      className="shrink-0 rounded p-1 text-muted-foreground hover:bg-secondary"
                      onClick={(e) => {
                        e.preventDefault();
                        handleMarkAsRead(n.id);
                      }}
                    >
                      <Check className="size-3.5" />
                    </button>
                  ) : null}
                </li>
              ))
            )}
          </ul>

          <div className="mt-1 border-t border-border pt-1">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="block rounded-sm px-2 py-1.5 text-center text-xs font-medium text-primary hover:bg-secondary/50"
            >
              Ver todas las notificaciones
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Merge live SSE notifications with the cached list, deduping by id.
 * Live notifications take precedence (newer) so an item marked-read
 * via API but already shown as unread on a fresh SSE event renders
 * with the SSE state until the cache invalidates.
 */
function mergeLatest(live: Notification[], cached: Notification[]): Notification[] {
  const byId = new Map<string, Notification>();
  for (const n of cached) byId.set(n.id, n);
  for (const n of live) byId.set(n.id, n);
  return Array.from(byId.values()).sort((a, b) =>
    a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0,
  );
}

function categoryLabel(category: Notification['category']): string {
  switch (category) {
    case 'DISPATCH_CREATED':
      return 'Despacho creado';
    case 'DISPATCH_READY':
      return 'Despacho listo';
    case 'PAYMENT_RECEIVED':
      return 'Pago recibido';
    case 'SYSTEM':
      return 'Sistema';
    default:
      return category;
  }
}

function relativeTime(iso: string): string {
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return '';
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'hace instantes';
  if (diff < 3_600_000) return `hace ${Math.round(diff / 60_000)} min`;
  if (diff < 86_400_000) return `hace ${Math.round(diff / 3_600_000)} h`;
  return new Date(iso).toISOString().slice(0, 10);
}
